/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useRef, useState } from 'react'
import { useThemeMode } from 'src/marketConnect/partials'
import { getCSS, getCSSVariableValue } from 'src/marketConnect/assets/ts/_utils'
import AppDatePickerRange from '../orders/components/AppDatePickerRange'
import moment from 'moment'
import { useAppSelector } from 'src/redux/storeHooks'
import ReactApexChart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'
import AppDatePickerWithCustomOptions from '../orders/components/AppDatePickerWithCustomOptions'
import { GRAPH_DATA } from 'src/app/_interfaces/orderDetail.interface'
import { addDays } from 'date-fns'
import Loader from 'src/utils/Loader'

type Props = {
  className: string
  loading: boolean
  filters: {
    from_date: string;
    to_date: string;
    platform_id: string;
    filter_type: string;
  }
  setFilters: React.Dispatch<React.SetStateAction<{
    from_date: string;
    to_date: string;
    platform_id: string;
    filter_type: string;
  }>>
  graphData: GRAPH_DATA[]
}

const OrderReportChart: React.FC<Props> = ({ className, filters, setFilters, graphData, loading = false }) => {
  const labelColor = getCSSVariableValue('--bs-gray-500')
  const borderColor = getCSSVariableValue('--bs-gray-200')
  const baseColor = getCSSVariableValue('--bs-info')
  const lightColor = getCSSVariableValue('--bs-info-light')

  const chartRef = useRef<HTMLDivElement | null>(null)
  const [isDatePickerVisible, setIsDatePickerVisible] = useState<boolean>(false)
  const [selectedPlatform, setSelectedPlatform] = useState('')
 const [selectedDate, setSelectedDate] = useState({
    from_date: moment(addDays(new Date(), -29)).utc(true).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
    to_date: moment(new Date()).utc(true).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'), label: 'Last 30 Days'
  })
  const { fetchedConnectedPlatformData } = useAppSelector((state) => state.fetchConnectedPlatform)
  const { fetchedUserAccountLoading, fetchedUserAccountData } = useAppSelector(
    (state) => state.userAccountData
  )

  const [chartData, setChartData] = useState<number[]>([])
  const [categories, setCategories] = useState<string[]>([])
  const [maxYValue, setMaxYValue] = useState<number>(0)
  const [totalOrders, setOrders] = useState<number>(0);
  const datePickerRef = useRef<HTMLDivElement | null>(null)

  const formatDayLabel = (date: string) => moment(date).format('DD-MMM')

  useEffect(() => {
  const handleClickOutside = (event: MouseEvent) => {
    if ( datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
      setIsDatePickerVisible(false)
    }
  }
  if (isDatePickerVisible) {
    document.addEventListener('mousedown', handleClickOutside)
  }
  return () => {
    document.removeEventListener('mousedown', handleClickOutside)
  }
}, [isDatePickerVisible])


  useEffect(() => {
    if (graphData.length > 0) {
      const data = graphData.map(item => {
      const value = item.orders?.total_orders ?? 0; // fallback to 0 if missing
      const numberValue = Number(String(value).replace(/,/g, ""));
      return Number.isFinite(numberValue) ? numberValue : 0; // ensure valid number
    });
      const days = graphData.map(item => formatDayLabel(item.day))
      setChartData(data)
      setCategories(days)
      const maxOrderNumber = Math.max(...data)
      setMaxYValue(maxOrderNumber)
      const total = data.reduce((sum, val) => sum + val, 0);
      setOrders(total);
    }
  }, [graphData])

  const options: ApexOptions = {
    series: [
      {
        name: 'Orders',
        data: chartData,
      },
    ],
    chart: {
      fontFamily: 'inherit',
      type: 'line',
      height: 300,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    markers: {
      colors: '#0f6dfa',
      size: 5,
      hover: {
        size: 10
      }

    },
    stroke: {
      curve: 'smooth',
      show: true,
      width: 3,
      colors: [baseColor],
    },
    xaxis: {
      categories: categories,
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      min: 0,
      max: maxYValue + 5,
      seriesName: 'Orders',
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
        formatter: (value) => Math.ceil(value) + '',
      },
    },
    colors: [lightColor],
    grid: {
      borderColor: borderColor,
      strokeDashArray: 2,
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
  }
  console.log("selectedDate",selectedDate)

  return (
    <div className={`card ${className}`}>
      {/* Header */}
      <div
        className="card-header bg-white border-bottom border-0"
        style={{ padding: "1rem" }}
      >
        {/* MAIN FLEX ROW: LEFT + RIGHT */}
        <div className="d-flex justify-content-between align-items-start w-100">

          {/* LEFT SIDE: TITLE */}
          <h5 className="card-title fw-bold fs-4 mb-0">Order Report</h5>

          {/* RIGHT SIDE: DROPDOWN + BUTTONS */}
          <div className="d-flex flex-column align-items-end gap-2">

            {/* PLATFORM DROPDOWN */}
            <select
              value={selectedPlatform}
              className="form-select form-select-sm"
              style={{
                width: "305px",
                borderRadius: "8px",
                padding: "6px 10px",
                fontWeight: "600",
              }}
              onChange={(ev) => setSelectedPlatform(ev.target.value)}
            >
              <option value="">All Platforms</option>
              {fetchedConnectedPlatformData?.map((channel) => {
                if (
                  !(
                    channel?.isMainStore ||
                    channel?.isAccountingStore ||
                    channel?.isShippingStore
                  )
                ) {
                  return (
                    <option key={channel.id} value={channel.id}>
                      {channel.display_name}
                    </option>
                  );
                }
              })}
            </select>

            {/* DATE PICKER + APPLY + RESET */}
            <div className="d-flex gap-2">

              {/* DATE BUTTON */}
              <div className="position-relative" ref={datePickerRef}>
                <button
                  className="btn btn-sm text-dark border d-flex align-items-center"
                  onClick={() => setIsDatePickerVisible(!isDatePickerVisible)}
                  style={{
                    background: "#f8f9fa",
                    borderRadius: "8px",
                    padding: "6px 12px",
                    minWidth: "180px",
                    fontWeight: "600",
                    gap: "8px",
                  }}
                >
                  <span
                    style={{
                      width: "16px",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <i
                      className="bi bi-calendar"
                      style={{ fontSize: "14px", color: "#6c757d" }}
                    ></i>
                  </span>
                  <span style={{ flex: 1, textAlign: "left" }}>
                    {selectedDate.label && selectedDate.label !== "Custom"
                      ? selectedDate.label
                      : `${moment(selectedDate.from_date).format(
                        "YYYY/MM/DD"
                      )} - ${moment(selectedDate.to_date).format("YYYY/MM/DD")}`}
                  </span>
                </button>

                <AppDatePickerWithCustomOptions
                  isVisible={isDatePickerVisible}
                  handleClose={() => setIsDatePickerVisible(false)}
                  initialRange={{
                    startDate: selectedDate.from_date,
                    endDate: selectedDate.to_date,
                    label: selectedDate.label,
                  }}
                  onPickRange={({ startDate, endDate, label }) => {
                    const diff = moment(endDate)
                      .startOf("day")
                      .diff(moment(startDate).startOf("day"), "days") + 1;

                    const isCustom = label === "Custom" || diff > 45;

                    setSelectedDate({
                      from_date: moment(startDate).format("YYYY-MM-DD"),
                      to_date: moment(endDate).format("YYYY-MM-DD"),
                      label: isCustom ? "Custom" : label,
                    });
                  }}
                />
              </div>

              {/* APPLY BUTTON */}
              <button
                className="btn btn-sm btn-success fw-bold px-3 shadow-sm"
                onClick={() => {
                  setFilters({
                    from_date: selectedDate.from_date,
                    to_date: selectedDate.to_date,
                    platform_id: selectedPlatform,
                    filter_type: "",
                  });
                }}
                disabled={fetchedUserAccountData?.role?.report_access === "READ"}
                style={{ borderRadius: "8px" }}
              >
                Apply
              </button>

              {/* RESET BUTTON */}
              <button
                className="btn btn-sm btn-warning fw-bold px-3 shadow-sm"
                onClick={() => {
                  setFilters({
                    from_date: moment(addDays(new Date(), -29))
                      .utc(true)
                      .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
                    to_date: moment(new Date())
                      .utc(true)
                      .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
                    platform_id: "",
                    filter_type: "",
                  });
                  setSelectedDate({
                    from_date: moment(addDays(new Date(), -29))
                      .utc(true)
                      .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
                    to_date: moment(new Date())
                      .utc(true)
                      .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
                    label: "Last 30 Days",
                  });
                  setSelectedPlatform("");
                }}
                disabled={fetchedUserAccountData?.role?.report_access === "READ"}
                style={{ borderRadius: "8px" }}
              >
                Reset
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* KPI / Total Orders */}
      <div style={{paddingTop: "4px" , display: "flex",justifyContent: "center",}}>
        <div
          style={{
            padding: "4px 5px",
            width: "auto",
            maxWidth: "max-content",
            textAlign: "center",
            whiteSpace: "nowrap",maxHeight:"4rem"
          }}
        >
          {/* TOP LABEL */}
          <div
            className="fw-semibold"
            style={{ color: "#6c757d", fontSize: "0.8rem" }}
          >
            Total Orders
          </div>

          {/* NUMBER BELOW */}
          <div
            className="fw-bold"
            style={{ fontSize: "1.5rem", color: "#0d6efd", marginTop: "-6px" }}
          >
            {totalOrders}
            {/* 56789 */}
          </div>
        </div>
      </div>


      {/* === CHART === */}
      {
        loading ? (
          <div className='d-flex justify-content-center p-5 h-100 min-h-300px'>
            <Loader variant="section" />
          </div>
        ) : (
          <div ref={chartRef} style={{ height: "350px" }}>
            <ReactApexChart
              options={options}
              series={options.series}
              type="line"
              height={350}
            />
          </div>
        )
      }
      {/* end::Body */}
    </div>
  )
}

export default OrderReportChart
